import React, { useState, useEffect } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";

const isRouteActive = (route, pathname) => {
  if (route.subRoutes) {
    return route.subRoutes.some((subRoute) => pathname.startsWith(subRoute.route));
  }
  return pathname.includes(route.key);
};

export default function NavBarItems({ routes, open, openDrawer, closeDrawer }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const activeSection = routes.find((route) => isRouteActive(route, location.pathname));
    if (activeSection) {
      setExpanded(activeSection.key);
    }
  }, [location.pathname, routes]);

  const toggleExpand = (key) => {
    setExpanded((prevKey) => (prevKey === key ? null : key));
  };

  const handleRouteClick = (route, hasSubScreens, event) => {
    if (!open && hasSubScreens) {
      openDrawer();
    } else if (!hasSubScreens) {
      navigate(route.route);
      closeDrawer();
    } else if (hasSubScreens) {
      toggleExpand(route.key);
    }
  };

  return (
    <List>
      {routes.filter(route => route.type === "collapse").map((route) => {
        const hasSubScreens = route.subRoutes?.length > 0;
        const isActive = isRouteActive(route, location.pathname);
        const isExpanded = expanded === route.key;

        return (
          <React.Fragment key={route.key}>
            <ListItem disablePadding sx={{ display: "block", position: "relative" }}>
              <ListItemButton
                component={hasSubScreens ? "div" : "a"}
                href={hasSubScreens ? undefined : route.route}
                onClick={(e) => {
                  if (hasSubScreens) {
                    handleRouteClick(route, hasSubScreens, e);
                  } else {
                    // For normal links, prevent default to handle via React Router
                    e.preventDefault();
                    navigate(route.route);
                    closeDrawer();
                  }
                }}
                sx={{
                  borderRadius: "12px",
                  minHeight: 48,
                  marginTop: 1,
                  marginLeft: 1.5,
                  marginRight: 1.5,
                  paddingLeft: 3,
                  paddingRight: 3,
                  backgroundColor: isActive ? "secondary.main" : "light.main",
                  color: "#161616",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: isActive ? "#E1EFFF!important" : "#E1EFFF!important",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 20, mr: open ? 2 : "auto", ml: open ? 1 : -1.3 }}>
                  {route.icon}
                </ListItemIcon>
                <ListItemText primary={route.name} sx={{ opacity: open ? 1 : 0 }} />
                {hasSubScreens && open && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>

            {hasSubScreens && open && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {route.subRoutes.map((subRoute) => {
                    if (subRoute.type === "hidden") return null;
                    const isSubActive = location.pathname.includes(subRoute.key);
                    return (
                      <ListItemButton
                        key={subRoute.key}
                        component="a"
                        href={subRoute.route}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(subRoute.route);
                          closeDrawer();
                        }}
                        sx={{
                          borderRadius: "12px",
                          minHeight: 48,
                          marginTop: 0.5,
                          marginLeft: 1.5,
                          marginRight: 1.5,
                          paddingLeft: 8,
                          paddingRight: 3,
                          backgroundColor: isSubActive ? "secondary.main" : "light.main",
                          color: "#161616",
                          fontWeight: 500,
                          "&:hover": { backgroundColor: "#E1EFFF!important" },
                        }}
                      >
                        <ListItemText primary={subRoute.name} sx={{ marginLeft: 1 }} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
}